import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraEmptyState, SpecraOverlayDialog } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { projectDataClient } from '../../../lib/connect/clients';
import { CustomerThemeEvidence } from '../CustomerThemeEvidence';
import { RowActionsMenu } from '../record/RowActionsMenu';
import { WinThemeClaims } from './WinThemeClaims';
import { WinThemeForm } from './WinThemeForm';
import { useWinThemeHistory, useWinThemes } from './winThemeHooks';
const when = (value)=>value ? value.toDate().toLocaleString() : '';
function ThemeFacts({ facts }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 text-sm",
        children: [
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: "Customer need:"
                    }),
                    " ",
                    facts.needSummary
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "whitespace-pre-wrap",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: "Differentiator:"
                    }),
                    " ",
                    facts.differentiator
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "whitespace-pre-wrap",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: "Narrative:"
                    }),
                    " ",
                    facts.narrative
                ]
            }),
            /*#__PURE__*/ _jsx(WinThemeClaims, {
                claims: facts.claims
            })
        ]
    });
}
function Lineage({ pursuitId, customerThemeId, versionId }) {
    const detail = useQuery({
        queryKey: [
            'customer-theme',
            pursuitId,
            customerThemeId
        ],
        queryFn: ()=>projectDataClient.getCustomerTheme({
                captureProjectId: pursuitId,
                themeId: customerThemeId
            })
    });
    const version = detail.data?.versions.find((v)=>v.id === versionId);
    return /*#__PURE__*/ _jsx("p", {
        className: "text-sm text-muted-foreground",
        children: version ? `Derived from revision v${version.version}` : 'Derived from a Customer Theme revision'
    });
}
function History({ pursuitId, theme, onClose }) {
    const history = useWinThemeHistory(pursuitId, theme.id);
    const versions = [
        ...history.data?.versions ?? []
    ].reverse();
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsxs("p", {
                className: "text-sm",
                children: [
                    "Version history for ",
                    theme.customerThemeTitle,
                    ". Earlier versions are read-only."
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "ghost",
                "data-testid": Testids.winThemes.closeHistory,
                onClick: onClose,
                children: "Close history"
            }),
            history.isPending && /*#__PURE__*/ _jsx("output", {
                children: "Loading version history…"
            }),
            history.isError && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    "Version history could not be loaded.",
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        onClick: ()=>history.refetch(),
                        children: "Retry"
                    })
                ]
            }),
            versions.map((version)=>/*#__PURE__*/ _jsxs("article", {
                    "data-testid": Testids.winThemes.versionItem,
                    className: "space-y-2 rounded-md border p-3",
                    children: [
                        /*#__PURE__*/ _jsxs("p", {
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraBadge, {
                                    "data-testid": Testids.winThemes.versionBadge,
                                    children: [
                                        "v",
                                        version.version
                                    ]
                                }),
                                ' ',
                                version.createdBy,
                                " · ",
                                when(version.createdAt),
                                version.version === theme.version ? ' · latest' : ' · read-only'
                            ]
                        }),
                        /*#__PURE__*/ _jsx(ThemeFacts, {
                            facts: version
                        })
                    ]
                }, version.id))
        ]
    });
}
/** Versioned Win Themes derived from accepted Customer Themes. */ export function WinThemesPanel({ pursuitId, canWrite, onOpenCustomerThemes, showTitle = true }) {
    const list = useWinThemes(pursuitId);
    const [form, setForm] = useState(null);
    const [historyId, setHistoryId] = useState('');
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": Testids.winThemes.panel,
        className: "space-y-3 rounded-md border p-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-2",
                children: [
                    showTitle && /*#__PURE__*/ _jsx("h3", {
                        className: "font-semibold",
                        children: "Win Themes"
                    }),
                    canWrite && !!list.data?.length && /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        "data-testid": Testids.pursuitWinThemes.addButton,
                        onClick: ()=>setForm({}),
                        children: "Add win theme"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(CustomerThemeEvidence, {
                receiver: "win_themes",
                objectId: pursuitId,
                readOnly: !canWrite
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm",
                children: "What we will tell the customer, tied to a reviewed Customer Theme. Claims without proof are flagged as Unsupported."
            }),
            list.isPending && /*#__PURE__*/ _jsx("output", {
                "data-testid": Testids.winThemes.loading,
                children: "Loading Win Themes…"
            }),
            list.isError && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                "data-testid": Testids.winThemes.error,
                children: [
                    "Win Themes could not be loaded.",
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.winThemes.retry,
                        onClick: ()=>list.refetch(),
                        children: "Retry Win Themes"
                    })
                ]
            }),
            form && /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                open: true,
                onOpenChange: (open)=>!open && setForm(null),
                title: form.theme ? 'Edit Win Theme' : 'Add win theme',
                size: "lg",
                testId: Testids.pursuitWinThemes.dialog,
                children: /*#__PURE__*/ _jsx(WinThemeForm, {
                    pursuitId: pursuitId,
                    theme: form.theme,
                    onCancel: ()=>setForm(null),
                    onSaved: ()=>setForm(null),
                    onOpenCustomerThemes: onOpenCustomerThemes
                }, form.theme?.id ?? 'new')
            }),
            list.data && !list.data.length && /*#__PURE__*/ _jsx(SpecraEmptyState, {
                size: "compact",
                title: "No Win Themes yet.",
                description: "Turn what the customer cares about into your pitch.",
                "data-testid": Testids.pursuitWinThemes.emptyState,
                action: canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    "data-testid": Testids.pursuitWinThemes.addButton,
                    onClick: ()=>setForm({}),
                    children: "Add win theme"
                }) : undefined
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-3",
                children: (list.data ?? []).map((theme)=>/*#__PURE__*/ _jsxs("li", {
                        "data-testid": Testids.winThemes.item,
                        className: "space-y-2 rounded-md border p-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-start justify-between gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "font-medium",
                                        children: theme.customerThemeTitle
                                    }),
                                    /*#__PURE__*/ _jsx(RowActionsMenu, {
                                        rowName: theme.customerThemeTitle,
                                        readOnly: !canWrite,
                                        onEdit: {
                                            label: 'Edit (saves a new version)',
                                            testId: Testids.winThemes.editButton,
                                            onSelect: ()=>setForm({
                                                    theme
                                                })
                                        }
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Lineage, {
                                pursuitId: pursuitId,
                                customerThemeId: theme.customerThemeId,
                                versionId: theme.customerThemeVersionId
                            }),
                            theme.staleLineage && /*#__PURE__*/ _jsx("output", {
                                "data-testid": Testids.winThemes.staleNotice,
                                className: "block text-sm",
                                children: "The Customer Theme has been revised since this version."
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                className: "text-sm",
                                children: [
                                    /*#__PURE__*/ _jsxs(SpecraBadge, {
                                        "data-testid": Testids.winThemes.versionBadge,
                                        children: [
                                            "v",
                                            theme.version
                                        ]
                                    }),
                                    ' ',
                                    "by ",
                                    theme.createdBy,
                                    " · ",
                                    when(theme.createdAt)
                                ]
                            }),
                            /*#__PURE__*/ _jsx(ThemeFacts, {
                                facts: theme
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                "data-testid": Testids.winThemes.historyButton,
                                onClick: ()=>setHistoryId(historyId === theme.id ? '' : theme.id),
                                children: historyId === theme.id ? 'Hide version history' : 'Version history'
                            }),
                            historyId === theme.id && /*#__PURE__*/ _jsx(History, {
                                pursuitId: pursuitId,
                                theme: theme,
                                onClose: ()=>setHistoryId('')
                            })
                        ]
                    }, theme.id))
            })
        ]
    });
}
